// Application logging utility using Pino with PostHog Integration
// Use this for all application logging instead of console.log/console.error  
// Usage: logger.info({ key: value }, 'Message') or logger.info('Message')

import pino from 'pino'

// Minimal PostHog interface to avoid importing from posthog-node
interface PostHogServerClient {
  capture(params: {
    distinctId: string
    event: string
    properties?: Record<string, any>
  }): void
  shutdown(): Promise<void>
}

// Create the base logger with pino
const isDevelopment = process.env.NODE_ENV === 'development'
const isServer = typeof window === 'undefined'

// Initialize PostHog client (singleton, server-side only)
let posthogClient: PostHogServerClient | null = null

function getPostHogClient(): PostHogServerClient | null {
  // Only use PostHog on server-side
  if (!isServer) {
    return null
  }

  // Don't send to PostHog in development
  if (isDevelopment) {
    return null
  }

  if (!process.env.POSTHOG_API_KEY) {
    return null // PostHog not configured
  }

  if (!posthogClient) {
    // Dynamic import to avoid loading on client-side
    import('posthog-node').then(({ PostHog }) => {
      posthogClient = new PostHog(process.env.POSTHOG_API_KEY!, {
        host: process.env.POSTHOG_HOST || 'https://eu.i.posthog.com',
        flushAt: 20, // Batch events
        flushInterval: 10000, // Flush every 10 seconds
      })
    }).catch(() => {
      // Silently fail if PostHog can't be loaded
    })
  }

  return posthogClient
}

// Custom Pino stream that forwards error and warn logs to PostHog (server-side only)
const posthogStream: pino.StreamEntry = {
  level: 'warn', // Only forward warn and error logs to PostHog
  stream: {
    write: (log: string) => {
      if (!isServer) return // Only on server

      try {
        const logObj = JSON.parse(log)
        const posthog = getPostHogClient()

        if (!posthog) return

        const level = logObj.level
        const msg = logObj.msg

        // Map pino levels to severity
        let severity = 'info'
        if (level >= 60) severity = 'fatal' // fatal level
        else if (level >= 50) severity = 'error' // error level
        else if (level >= 40) severity = 'warning' // warn level

        // Only send warn, error, and fatal to PostHog
        if (level >= 40) {
          posthog.capture({
            distinctId: 'main-app-service',
            event: 'app_log',
            properties: {
              level: severity,
              message: msg,
              context: logObj.context,
              error: logObj.error,
              ...logObj,
              timestamp: new Date().toISOString(),
              service: 'main-app',
            },
          })
        }
      } catch (err) {
        // Silently fail - don't let logging errors break the app
      }
    },
  },
}

// Base logger configuration
const baseConfig: pino.LoggerOptions = {
  level: isDevelopment ? 'debug' : 'info',
  browser: {
    asObject: true,
  },
}

// Create logger based on environment and platform
export const logger =
  isDevelopment && isServer
    ? // Development server: pretty printing
      pino({
        ...baseConfig,
        transport: {
          target: 'pino-pretty',
          options: {
            colorize: true,
            translateTime: 'HH:MM:ss',
            ignore: 'pid,hostname',
          },
        },
      })
    : isServer && !isDevelopment
      ? // Production server: multi-stream with PostHog
        pino(
          baseConfig,
          pino.multistream([
            { level: 'info', stream: process.stdout }, // All logs to stdout
            posthogStream, // Errors/warnings to PostHog
          ])
        )
      : // Browser: basic logger
        pino(baseConfig)

// Factory function to create context-specific loggers
export function createLogger(context: string) {
  return logger.child({ context })
}

// Graceful shutdown for PostHog (server-side only)
export async function shutdownLogger(): Promise<void> {
  if (!isServer) return

  const posthog = getPostHogClient()
  if (posthog) {
    await posthog.shutdown()
  }
}


